/**
 * LpReveal
 * スクロールに合わせて、ブロックを下から上へふわっと表示する。
 *
 * - 対象＝`.js-lp-reveal`。画面内に入ったら `is-inview` を付ける。
 * - **出すのは下りるときだけ**。戻すのは「画面の下へ完全に出た」ときに限る（ディレクター指定）。
 *   上へ抜けたものは付けたままにする＝上へ戻る途中で再生し直さない。
 *   上へスクロールしていくと、下端から画面外へ出たものが順に消えていく。
 *   出す判定と戻す判定で基準線が違うため、監視は2つに分けている
 *   （出す＝少し入ってから／戻す＝完全に出てから）。
 * - `data-reveal="stagger"` を付けたブロックは、**親ではなく直下の子を1つずつ監視**する。
 *   親を監視すると、親の上端が見えた時点で子が全部出てしまい、SPで縦積みになったときに
 *   画面外のカードまで表示済みになる（実測：4枚目が画面外のまま表示済みだった）。
 * - ずらし表示は「同じ親の中で同時に入った子」にだけ効かせる。
 *   PCの横並びは一斉に入るので順にずれ、SPの縦積みは1枚ずつ入るので遅延0で素直に出る。
 * - JSが動いた時だけ `<html>` に `has-lp-reveal` を付け、CSS側はその配下でのみ隠す。
 *   JS無効・エラー時に中身が消えたままになるのを防ぐ。
 * - `prefers-reduced-motion` では動かさず、最初から表示する。
 * - **`is-inview` と `--reveal-index` は `LpToggleMore` も触る。**あちらは閉じるときに
 *   隠す要素からこの2つを落とす（`display: none` になった要素は矩形が全部0になり、
 *   ここの「画面の下へ完全に出た」判定に引っかからないため、印が残って再生されない）。
 *   **この2つの名前を変えるときは `LpToggleMore` も直すこと**（エラーにならず静かに壊れる）。
 */

const ROOT_MARGIN = '0px 0px -15% 0px';
const STAGGER_ATTR = 'stagger';

export default class LpReveal {
  constructor() {
    const blocks = Array.from(document.querySelectorAll('.js-lp-reveal'));
    if (!blocks.length) {
      return;
    }

    this.items = blocks.reduce((acc, block) => {
      if (block.dataset.reveal === STAGGER_ATTR) {
        return acc.concat(Array.from(block.children));
      }
      acc.push(block);
      return acc;
    }, []);
    this.staggered = new Set(
      blocks
        .filter((block) => block.dataset.reveal === STAGGER_ATTR)
        .reduce((acc, block) => acc.concat(Array.from(block.children)), []),
    );

    document.documentElement.classList.add('has-lp-reveal');

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) {
      this.items.forEach((el) => el.classList.add('is-inview'));
      return;
    }

    this.observe();
  }

  observe() {
    this.showObserver = new IntersectionObserver((entries) => {
      const shown = entries.filter((entry) => entry.isIntersecting);
      if (!shown.length) {
        return;
      }
      // 同時に入ったものへ上から順に番号を振る。番号はCSSの transition-delay に渡す
      const orderByParent = new Map();
      shown
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        .forEach((entry) => {
          const el = entry.target;
          if (this.staggered.has(el)) {
            const parent = el.parentElement;
            const index = orderByParent.get(parent) || 0;
            orderByParent.set(parent, index + 1);
            el.style.setProperty('--reveal-index', index);
          }
          el.classList.add('is-inview');
        });
    }, { rootMargin: ROOT_MARGIN });

    // 画面の下へ完全に出たものだけ元へ戻す。top が正＝下端の外側にある、で判別できる
    this.hideObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting && entry.boundingClientRect.top > 0) {
          entry.target.classList.remove('is-inview');
        }
      });
    });

    this.items.forEach((el) => {
      this.showObserver.observe(el);
      this.hideObserver.observe(el);
    });
  }

  destroy() {
    if (this.showObserver) {
      this.showObserver.disconnect();
    }
    if (this.hideObserver) {
      this.hideObserver.disconnect();
    }
  }
}
